import { STRICT_COOKIE_POLICY_CONTRIES } from '@/utils/config'
import { StoreUtils } from '@/store/StoreUtil'

/**
 * @description cookie相关的操作, 包括了gdpr的一些业务
 * document相关操作禁止在服务端执行，如果初始化时要使用，请在onMounted里面执行
 * */

interface ConfigItem {
  configs: Array<{ name: string }>
  necessary?: boolean
  // 储存在localStorage里面的控制开关的变量
  id: string
}
type CookieConfiguration = Record<string, ConfigItem>

interface CookieStoreConfig {
  id: string
  isOn: string
}

const COOKIE_POLICY_STORE_KEY = 'cookiePolicy'

const MINUTE = 60
const HOUR = MINUTE * 60
const DAY = HOUR * 24
const WEEK = DAY * 7
const YEAR = DAY * 365

// max-age使用的标准时间，单位为s
const FORMAT_TIME = { MINUTE, HOUR, DAY, WEEK, YEAR }

// elearning中使用的cookie
const ELEARNING_COOKIE = {
  cookieDisclaimer: 'cookie-disclaimer', // 记录用户是否操作过gdpr相关
  atConfig: 'at-config', // at 埋点相关
  atuserid: 'atuserid',
  atidvisitor: 'atidvisitor',
  _ga: '_ga',
  _gaCodeTst: '_ga_01TXQ4CGJK',
  _gaCodeProd: '_ga_DTRH106XZH',
  _gid: '_gid',
  _gcl_au: '_gcl_au'
}

const COOKIE_STATUS = {
  true: '1',
  false: '2'
}

// cookie-manage的相关配置, 和CookieStore对应，主要和cookie相关操作有关
const COOKIE_MANAGE_CONFIGURATION: CookieConfiguration = {
  necessary: {
    necessary: true,
    id: 'necessary',
    configs: [
      { name: ELEARNING_COOKIE.cookieDisclaimer }
    ]
  },
  functionality: {
    id: 'functionality',
    configs: [
      { name: ELEARNING_COOKIE.atConfig }
    ]
  },
  analytics: {
    id: 'analytics',
    configs: [
      { name: ELEARNING_COOKIE.atuserid },
      { name: ELEARNING_COOKIE.atidvisitor },
      { name: ELEARNING_COOKIE._ga },
      { name: ELEARNING_COOKIE._gaCodeTst },
      { name: ELEARNING_COOKIE._gaCodeProd },
      { name: ELEARNING_COOKIE._gid },
      { name: ELEARNING_COOKIE._gcl_au }
    ]
  }
}

/**
 * @description 用户CookiePolicy操作本地记录,主要和localStorage操作有关
 * */
export class CookiePolicyStore {
  configs: CookieStoreConfig[] = []
  constructor (configs: CookieStoreConfig[]) {
    this.configs = configs
  }
  setCookieStore (config: CookieStoreConfig) {
    const index = this.configs.findIndex(item => config.id === item.id)
    if (index < 0) {
      this.configs.push(config)
    } else {
      this.configs[index] = config
    }
    localStorage.setItem(COOKIE_POLICY_STORE_KEY, JSON.stringify(this.configs))
  }
  saveCookieStore () {
    localStorage.setItem(COOKIE_POLICY_STORE_KEY, JSON.stringify(this.configs))
  }
}

export const StoreUtil = {
  /**
   * @description 获取用户历史保存的数据
  */
  getCookieStore (): CookiePolicyStore {
    // console.log('get cookie store', StoreUtils.store, StoreUtils.store?.state?.global?.isoCode)
    const isConfinedCountry = STRICT_COOKIE_POLICY_CONTRIES.includes(StoreUtils.store?.state?.global?.isoCode) ||
      StoreUtils.store?.state?.global?.isEU
    if (localStorage.getItem(COOKIE_POLICY_STORE_KEY)) {
      // 用户操作过，返回之前的操作结果
      const value = JSON.parse(localStorage.getItem(COOKIE_POLICY_STORE_KEY) || '') as CookieStoreConfig[]
      return new CookiePolicyStore(value)
    } else if (isConfinedCountry) {
      // 个别国家，默认关闭
      return this.rejectAllPolicy()
    } else {
      // 默认开启
      return this.acceptAllPolicy()
    }
  },
  /**
   * @description 生成configs
  */
  getConfigs (isOn: string) {
    const configs: CookieStoreConfig[] = Object
      .keys(COOKIE_MANAGE_CONFIGURATION)
      .map(key => COOKIE_MANAGE_CONFIGURATION[key])
      .filter(item => !item.necessary)
      .map(item => ({ id: item.id, isOn }))
    return configs
  },
  /**
   * @description 用户拒绝所有的cookye
  */
  rejectAllPolicy () {
    const configs = this.getConfigs(COOKIE_STATUS.false)
    const Store = new CookiePolicyStore(configs)
    Store.saveCookieStore()
    return Store
  },
  /**
   * @description 用户接受所有的cookye
  */
  acceptAllPolicy (): CookiePolicyStore {
    const configs = this.getConfigs(COOKIE_STATUS.true)
    const Store = new CookiePolicyStore(configs)
    Store.saveCookieStore()
    return Store
  }
}

export const CookieUtil = {
  setCookie (key: string, value: string, maxAge = YEAR) {
    if (!key) return
    document.cookie = `${key}=${value};max-age=${maxAge};path=/`
  },
  getCookie (key: string) {
    return decodeURIComponent(
      document.cookie.replace(new RegExp('(?:(?:^|.*;)\\s*' + encodeURIComponent(key).replace(/[-.+*]/g, '\\$&') + '\\s*\\=\\s*([^;]*).*$)|^.*$'), '$1')
    ) || null
  },
  getAllCookieKeys () {
    const cookie = document.cookie
    const cookieList = cookie.split(';') || []
    const keyList = cookieList.map(item => (item.split('=')[0] || '').trim()).filter(Boolean)
    return keyList
  },
  hasItem: function (key: string) {
    return (new RegExp('(?:^|;\\s*)' + encodeURIComponent(key).replace(/[-.+*]/g, '\\$&') + '\\s*\\=')).test(document.cookie)
  },
  removeItem (key: string) {
    const expire = new Date(0).toUTCString()
    const hostReg = new RegExp(/(\.\w+\.)(com|cn)/, 'gi')
    // 多租户下使用不同域名
    const domain = location.hostname.match(hostReg)?.[0] || ''
    console.log('on remove', key, this.hasItem(key), `${encodeURIComponent(key)}=;expires=${expire};path=/;domain=${domain}`)
    if (!key || !this.hasItem(key)) return
    document.cookie = `${encodeURIComponent(key)}=;expires=${expire};path=/;domain=${domain}` // 删除当前父域名下的cookie
    document.cookie = `${encodeURIComponent(key)}=;expires=${expire};path=/;` // 删除当前域下的cookie
  },
  /**
   * @description 接受cookie政策
   * 用户点击 accept/reject/manage/switch button时调用
  */
  updateCookieDisclaimer () {
    this.setCookie(ELEARNING_COOKIE.cookieDisclaimer, COOKIE_STATUS.true)
  },
  /**
   * @description 批量关闭和删除所有cookie
   * */
  deleteAllCookie () {
    const keys = Object.keys(COOKIE_MANAGE_CONFIGURATION)
    keys.filter(k => !COOKIE_MANAGE_CONFIGURATION[k].necessary).forEach(key => {
      this.deleteCookieById(key)
    })
  },
  /**
   * @description 关闭和删除特定key的cookie
  */
  deleteCookieById (key: string) {
    if (COOKIE_MANAGE_CONFIGURATION[key]) {
      COOKIE_MANAGE_CONFIGURATION[key].configs?.forEach(item => {
        this.removeItem(item.name)
      })
    }
  },
  COOKIE_MANAGE_CONFIGURATION,
  FORMAT_TIME,
  ELEARNING_COOKIE,
  COOKIE_STATUS
}
